import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { reportsAPI, usersAPI, storesAPI, menuAPI } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import {
  Package, Truck, Shield, Container, CheckSquare,
  ShoppingCart, AlertTriangle, Bell, TrendingUp, ChevronRight,
  CalendarCheck, Sparkles, ArrowDown, CalendarDays, School, BarChart3
} from "lucide-react";

const StatCard = ({ title, value, icon: Icon, color, onClick, alert }) => (
  <div
    onClick={onClick}
    className={`bg-white border border-[#E5E1DB] rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer animate-fade-in-up ${alert ? "border-[#D98032] bg-[#FDF4EB]" : ""}`}
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={20} />
      </div>
      {alert && <AlertTriangle size={16} className="text-[#D98032]" />}
    </div>
    <p className="text-2xl font-extrabold font-[Manrope] text-[#3A322C]">{value}</p>
    <p className="text-xs text-[#8A7E73] mt-1 font-medium">{title}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [todayEntry, setTodayEntry] = useState({ inward: 0, hasPlan: false, pricesSet: 0 });
  const [loading, setLoading] = useState(true);

  // Permission helper — super admin sees everything
  const isAdmin = user?.is_admin;
  const can = (perm) => isAdmin || !!user?.permissions?.[perm];

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const [statsRes, notifsRes, inwardRes, pricesRes] = await Promise.all([
        reportsAPI.getDashboardStats(),
        usersAPI.getNotifications(5),
        can("stores_view") ? storesAPI.getInward({ limit: 50 }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        isAdmin ? (await import("../utils/api")).adminAPI.getTargetPrices().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setStats(statsRes.data || {});
      setNotifications(notifsRes.data || []);
      const todayIn = (inwardRes.data || []).filter(r => {
        const d = r.date?.split("T")[0] || r.date?.slice(0, 10);
        return d === today;
      }).length;
      let hasPlan = false;
      try {
        const kitchens = await usersAPI.getKitchens();
        if (kitchens.data?.[0]?.id) {
          const plan = await (await import("../utils/api")).menuAPI.getPlanByDate(today, kitchens.data[0].id);
          hasPlan = !!plan.data;
        }
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
      setTodayEntry({ inward: todayIn, hasPlan, pricesSet: (pricesRes.data || []).length });
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  // Helper: checks both module-level perm AND sub-module view perm
  // subKey format matches navItems (e.g. "security_entry_exit" → sub_security_entry_exit_view)
  const canSub = (perm, subKey) => {
    if (!can(perm)) return false;
    if (!subKey) return true;
    const subPerm = `sub_${subKey}_view`;
    // If explicitly set to false by admin → hide; if undefined → default show
    return user?.permissions?.[subPerm] !== false;
  };

  // Stat cards — filtered by module perm + sub-module perm
  const statCards = useMemo(() => [
    { title: "Gate Entries Today",    value: stats.today_entries ?? 0,          icon: Shield,       color: "bg-blue-50 text-blue-600",    path: "/gate-log",              perm: "security_view",   subKey: "security_gate_log" },
    { title: "Pending Tasks",         value: stats.pending_tasks ?? 0,           icon: CheckSquare,  color: "bg-purple-50 text-purple-600", path: "/tasks",                 perm: null },
    { title: "Pending Indents",       value: stats.pending_indents ?? 0,         icon: ShoppingCart, color: "bg-amber-50 text-amber-600",  path: "/purchase?tab=indents",  perm: "purchase_view",   subKey: "purchase_indents",   alert: (stats.pending_indents ?? 0) > 0 },
    { title: "Pending POs",           value: stats.pending_purchase_orders ?? 0, icon: TrendingUp,   color: "bg-green-50 text-green-600",  path: "/purchase?tab=orders",   perm: "purchase_view",   subKey: "purchase_orders" },
    { title: "Outstanding Vessels",   value: stats.outstanding_vessels ?? 0,     icon: Container,    color: "bg-orange-50 text-orange-600", path: "/vessels",               perm: "security_view",   subKey: "ops_vessels",        alert: (stats.outstanding_vessels ?? 0) > 0 },
    { title: "Low Stock Items",       value: stats.low_stock_items ?? 0,         icon: Package,      color: "bg-red-50 text-red-600",       path: "/stores?tab=alerts",     perm: "stores_view",     subKey: "stores_alerts",      alert: (stats.low_stock_items ?? 0) > 0 },
    { title: "Routes Active",         value: stats.active_routes ?? 0,           icon: Truck,        color: "bg-teal-50 text-teal-600",     path: "/logistics",             perm: "logistics_view",  subKey: "logistics_routes" },
    { title: "Schools Today",         value: stats.schools_served_today ?? 0,    icon: School,       color: "bg-indigo-50 text-indigo-600", path: "/school-indent",         perm: null,              subKey: "ops_school_indent" },
  ].filter(c => canSub(c.perm, c.subKey)), [stats, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quick Actions — filtered by both module perm AND sub-module perm
  // subKey must match navItems subKey so admin matrix changes reflect here instantly
  const quickActions = useMemo(() => [
    { label: "Record Gate Entry",   sub: "Security / Inward or Outward",  icon: Shield,       path: "/security",              color: "text-blue-500",    perm: "security_create", subKey: "security_entry_exit"  },
    { label: "Stock Inward Entry",  sub: "Add new stock receipt",          icon: Package,      path: "/stores?tab=inward",     color: "text-green-500",   perm: "stores_create",   subKey: "stores_inward"        },
    { label: "Issue Stock",         sub: "Issue items from store",         icon: Truck,        path: "/stores?tab=issue",      color: "text-amber-500",   perm: "stores_create",   subKey: "stores_issued"        },
    { label: "School Indent",       sub: "Enter today school strength",    icon: School,       path: "/school-indent",         color: "text-indigo-500",  perm: null,              subKey: "ops_school_indent"    },
    { label: "Create Task",         sub: "Assign work to team",            icon: CheckSquare,  path: "/tasks",                 color: "text-purple-500",  perm: null                                                },
    { label: "Purchase Request",    sub: "Raise a purchase indent",        icon: ShoppingCart, path: "/stores?tab=indent",     color: "text-teal-500",    perm: "stores_view",     subKey: "stores_purchase_req"  },
    { label: "Vessel Summary",      sub: "Check outstanding vessels",      icon: Container,    path: "/vessels",               color: "text-orange-500",  perm: "security_view",   subKey: "ops_vessels"          },
    { label: "Logistics Routes",    sub: "View routes and coordinators",   icon: Truck,        path: "/logistics",             color: "text-cyan-500",    perm: "logistics_view",  subKey: "logistics_routes"     },
    { label: "Purchase Orders",     sub: "View and manage POs",            icon: TrendingUp,   path: "/purchase?tab=orders",   color: "text-emerald-500", perm: "purchase_view",   subKey: "purchase_orders"      },
    { label: "View Reports",        sub: "Analytics and reports",          icon: BarChart3,    path: "/reports",               color: "text-pink-500",    perm: "reports_view",    subKey: "reports_main"         },
  ].filter(a => canSub(a.perm, a.subKey)), [user]); // eslint-disable-line react-hooks/exhaustive-deps


  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-7xl" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">
          Good {new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 17 ? "Afternoon" : "Evening"}, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-[#8A7E73] mt-1">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card, i) => (
          <StatCard key={`item-${i}`} {...card} onClick={() => navigate(card.path)} />
        ))}
      </div>

      {/* Today Data Entry Status */}
      <div
        onClick={() => navigate("/daily-entry")}
        className="bg-gradient-to-r from-[#E8956D]/10 to-[#5C5147]/5 border border-[#E8956D]/30 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#E8956D] rounded-xl flex items-center justify-center">
              <CalendarCheck size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-[#3A322C]">Today Data Entry</p>
              <p className="text-xs text-[#8A7E73]">Click to open Daily Entry Hub</p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {[
              { label: "Stock Inward Entries", value: todayEntry.inward, ok: todayEntry.inward > 0, icon: ArrowDown },
              { label: "Menu Plan", value: todayEntry.hasPlan ? "Set" : "Missing", ok: todayEntry.hasPlan, icon: CalendarDays },
              { label: "Target Prices", value: `${todayEntry.pricesSet} items`, ok: todayEntry.pricesSet > 0, icon: TrendingUp },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2">
                <s.icon size={14} className={s.ok ? "text-[#637C64]" : "text-[#D98032]"} />
                <div>
                  <p className={`text-sm font-bold ${s.ok ? "text-[#637C64]" : "text-[#D98032]"}`}>{s.value}</p>
                  <p className="text-[10px] text-[#8A7E73]">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Sparkles size={14} className="text-[#E8956D]" />
            <span className="text-xs text-[#E8956D] font-bold">Powers AI Analytics</span>
            <ChevronRight size={16} className="text-[#C4BAAF]" />
          </div>
        </div>
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts */}
        <div className="bg-white border border-[#E5E1DB] rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#F4F3F0]">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-[#E8956D]" />
              <h3 className="font-bold text-[#3A322C]">Recent Alerts</h3>
            </div>
            {(stats.unread_notifications ?? 0) > 0 && (
              <span className="bg-[#C2624E] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {stats.unread_notifications} new
              </span>
            )}
          </div>
          <div className="divide-y divide-[#F4F3F0]">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-[#8A7E73] text-sm">No recent alerts</div>
            ) : notifications.map(n => (
              <div key={n.id} className={`px-5 py-4 ${!n.is_read ? "bg-[#FDF4EB]" : ""}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={16} className={`mt-0.5 flex-shrink-0 ${n.type === "warning" ? "text-[#D98032]" : "text-[#637C64]"}`} />
                  <div>
                    <p className="text-sm text-[#3A322C]">{n.message}</p>
                    <p className="text-xs text-[#8A7E73] mt-1 uppercase tracking-wide">{n.module}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions — filtered by user permissions */}
        <div className="bg-white border border-[#E5E1DB] rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F4F3F0]">
            <h3 className="font-bold text-[#3A322C]">Quick Actions</h3>
          </div>
          <div className="divide-y divide-[#F4F3F0]">
            {quickActions.length === 0 ? (
              <p className="px-5 py-4 text-sm text-[#8A7E73]">No quick actions available for your role.</p>
            ) : quickActions.map((action, i) => (
              <button
                key={`idx-${i}`}
                onClick={() => navigate(action.path)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[#FAFAF8] transition-colors text-left"
              >
                <div className={`w-9 h-9 rounded-lg bg-[#F4F3F0] flex items-center justify-center ${action.color}`}>
                  <action.icon size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#3A322C]">{action.label}</p>
                  <p className="text-xs text-[#8A7E73]">{action.sub}</p>
                </div>
                <ChevronRight size={16} className="text-[#C4BAAF]" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
